import { create } from "zustand";
import { persist } from "zustand/middleware";
import { supabase } from "@/integrations/supabase/client";
import {
  identifyUser,
  resetAnalyticsUser,
  analyticsEvents,
} from "@/services/analytics";
import { User } from "@supabase/supabase-js";

export type UserRole = "staff" | "manager" | "admin" | "super_admin";

interface AuthDbUser extends User {
  first_name?: string;
  last_name?: string;
  fcm_token?: string;
  name?: string;
  storeIds?: string[];
  role?: UserRole | string;
  phone?: string;
  email?: string;
  id: string;
}

interface AuthState {
  user: Omit<
    AuthDbUser,
    "app_metadata" | "user_metadata" | "aud" | "created_at" | "updated_at"
  > | null;
  isAuthenticated: boolean;
  token: string | null;
  login: (
    user:
      | User
      | Omit<
          AuthDbUser,
          "app_metadata" | "user_metadata" | "aud" | "created_at" | "updated_at"
        >,
    token: string
  ) => void;
  logout: (navigate?: (path: string) => void) => Promise<void>;
  updateUser: (
    updatedUser: Partial<
      Omit<
        AuthDbUser,
        "app_metadata" | "user_metadata" | "aud" | "created_at" | "updated_at"
      >
    >
  ) => void;
  refreshStoreAccess: (userId: string) => Promise<string[]>;
  refreshUserData: (currentUser: AuthDbUser, session: string) => Promise<void>;
  cleanupAuthState: () => void;
  setUser: (
    user: Omit<
      AuthDbUser,
      "app_metadata" | "user_metadata" | "aud" | "created_at" | "updated_at"
    >
  ) => void;
}

// Helper function to clean up auth state
const cleanupAuthState = () => {
  // Remove any lingering auth tokens
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("supabase.auth.") || key.includes("sb-")) {
      localStorage.removeItem(key);
    }
  });
  // Also check sessionStorage if in use
  Object.keys(sessionStorage || {}).forEach((key) => {
    if (key.startsWith("supabase.auth.") || key.includes("sb-")) {
      sessionStorage.removeItem(key);
    }
  });
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      token: null,
      login: (
        user:
          | User
          | Omit<
              AuthDbUser,
              | "app_metadata"
              | "user_metadata"
              | "aud"
              | "created_at"
              | "updated_at"
            >,
        token: string
      ) => {
        if (!user || !token) {
          return;
        }

        set({
          user: user as Omit<
            AuthDbUser,
            | "app_metadata"
            | "user_metadata"
            | "aud"
            | "created_at"
            | "updated_at"
          >,
          isAuthenticated: true,
          token,
        });

        // Identify user in analytics
        const userName =
          "first_name" in user
            ? user.first_name + " " + user.last_name
            : ("name" in user ? user.name : "") || "";
        identifyUser(user.id, {
          name: userName,
          email: user.email,
          role: user.role,
        });
      },
      logout: async (navigate?: (path: string) => void) => {
        try {
          analyticsEvents.auth.logout();

          // Clear store state first

          // Reset analytics
          resetAnalyticsUser();

          // Clear FCM token from database
          const { error: fcmError } = await supabase
            .from("users")
            .update({ fcm_token: null })
            .eq("id", get().user?.id);
          if (fcmError) throw fcmError;

          set({ user: null, isAuthenticated: false, token: null });

          // Sign out from Supabase
          const { error } = await supabase.auth.signOut();
          if (error) throw error;

          // Clean up any remaining auth state
          // cleanupAuthState();

          // Navigate after everything is cleared
          if (navigate) navigate("/login");

          return Promise.resolve();
        } catch (error) {
          console.error("Error during logout:", error);
          return Promise.reject(error);
        }
      },

      setUser: async (
        user: Omit<
          AuthDbUser,
          "app_metadata" | "user_metadata" | "aud" | "created_at" | "updated_at"
        >
      ) => {
        // set({ user });
        const userData = await fetchUserData(user.id);
        set({ user: userData });
      },
      updateUser: (updatedUser) => {
        set((state) => {
          const newUser = state.user ? { ...state.user, ...updatedUser } : null;

          // Update user identity in analytics if user exists
          if (newUser) {
            identifyUser(newUser.id, {
              name: newUser.name,
              email: newUser.email,
              role: newUser.role,
            });
          }

          return { user: newUser };
        });
      },
      refreshStoreAccess: async (userId) => {
        try {
          // Get stores this user is associated with
          const { data: storeEmployees, error: storeError } = await supabase
            .from("store_employees")
            .select("store_id")
            .eq("user_id", userId);

          const storeIds = storeError
            ? []
            : storeEmployees.map((se) => se.store_id);

          // Update the user's storeIds
          set((state) => ({
            user: state.user ? { ...state.user, storeIds } : null,
          }));

          analyticsEvents.auth.login();

          return Promise.resolve(storeIds);
        } catch (error) {
          console.error("Error refreshing store access:", error);
          return Promise.reject(error);
        }
      },
      refreshUserData: async (currentUser: AuthDbUser) => {
        try {
          await fetchUserData(currentUser.id);
        } catch (error) {
          console.error("Error refreshing user data:", error);
        }
      },
      cleanupAuthState,
    }),
    {
      name: "cultrypay-auth",
      // Only persist these fields
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Initialize auth listener to keep auth state in sync with Supabase
export const initializeAuthListener = () => {
  // Check for existing session
  supabase.auth.getSession().then(({ data: { session } }) => {
    if (session) {
      // Fetch user data from our users table
      fetchUserData(session.user.id);
    }
  });

  // Set up auth state listener
  supabase.auth.onAuthStateChange(async (event, session) => {
    if (event === "SIGNED_IN" && session) {
      try {
        useAuthStore.getState().setUser(session.user);
      } catch (error) {
        console.error("Error in auth state change handler:", error);
      }
    } else if (event === "SIGNED_OUT") {
      // Clear auth store state
      useAuthStore.getState().cleanupAuthState();
      useAuthStore.getState().logout();
      window.location.href = "/login";
    } else if (event === "TOKEN_REFRESHED" && session) {
      // Update the store with the new token
      const { user } = useAuthStore.getState();
      if (user) {
        useAuthStore.getState().login(user, session.access_token);
      }
    } else if (event === "INITIAL_SESSION" && session === null) {
      useAuthStore.getState().cleanupAuthState();
    }
  });
};

// Helper function to fetch user data
const fetchUserData = async (userId: string) => {
  try {
    // Get user basic info
    const { data: userData, error } = await supabase
      .from("users")
      .select("*")
      .eq("id", userId)
      .single();
    if (error || !userData) {
      console.error("Error fetching user data:", error);
      return;
    }

    // Get stores this user is associated ith
    const { data: storeEmployees, error: storeError } = await supabase
      .from("store_employees")
      .select("store_id")
      .eq("user_id", userId);

    const storeIds = storeError
      ? []
      : storeEmployees?.map((se) => se.store_id) || [];

    const user = {
      ...userData,
      name: `${userData.first_name} ${userData.last_name}`,
      storeIds: storeIds,
    };
    identifyUser(user.id, {
      name: user.name,
      email: user.email,
      role: user.role,
    });
    return user;
  } catch (error) {
    console.error("Error in fetchUserData:", error);
  }
};
